import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import omnichannelService, { Conversation } from '../services/omnichannelService';
import { useOmnichannelWebSocket } from '../hooks/useOmnichannelWebSocket';

interface OmnichannelContextType {
  unreadCount: number;
  pendingCount: number;
  refreshCounts: () => Promise<void>;
}

const OmnichannelContext = createContext<OmnichannelContextType | undefined>(undefined);

export function OmnichannelProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshCounts = useCallback(async () => {
    try {
      const response = await omnichannelService.getConversations(
        { status: ['pending', 'assigned'] },
        1,
        1
      );
      
      // Calculate unread from conversations
      const conversations = response.data as Conversation[];
      const totalUnread = conversations.reduce(
        (sum, conv) => sum + conv.unread_count,
        0
      );
      setUnreadCount(totalUnread);

      // Get pending count
      const pendingResponse = await omnichannelService.getConversations(
        { status: ['pending'] },
        1,
        1
      );
      setPendingCount(pendingResponse.pagination.total as number);
    } catch (error) {
      console.error('Failed to refresh omnichannel counts:', error);
    }
  }, []);

  // Set up WebSocket listener for real-time updates
  useOmnichannelWebSocket({
    onNewMessage: refreshCounts,
    onConversationUpdated: refreshCounts
  });

  // Initial load
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  return (
    <OmnichannelContext.Provider value={{ unreadCount, pendingCount, refreshCounts }}>
      {children}
    </OmnichannelContext.Provider>
  );
}

export function useOmnichannel() {
  const context = useContext(OmnichannelContext);
  if (context === undefined) {
    throw new Error('useOmnichannel must be used within an OmnichannelProvider');
  }
  return context;
}
